(() => {
  'use strict';
  if (window.abcProductPreviewUI) return;
  window.abcProductPreviewUI = true;
  const instances = new WeakMap();
  const labels = {
    en: {
      front: 'Front', back: 'Back', look: 'Wearing view', enlarge: 'Enlarge photograph', shownIn: 'Shown in', viewColor: 'View color',
      chooseSize: 'Select a size', add: 'Add to cart', adding: 'Adding…', viewCart: 'View cart', soldOut: 'Sold out', notAvailable: 'Unavailable', checking: 'Checking availability…', added: 'Added to your cart.',
      size: 'Size', length: 'Length', shoulder: 'Shoulder', chest: 'Chest', sleeve: 'Sleeve',
      cm: 'Measurements in centimeters', inch: 'Measurements in inches',
      unavailable: 'Product information is temporarily unavailable. Please try again shortly.',
      addError: 'Unable to add this item. Please try again.', cartUncertain: 'We could not confirm the update. Please check your cart before trying again.', quantityError: 'Choose a whole quantity of at least 1.',
    },
    ja: {
      front: '正面', back: '背面', look: '着用写真', enlarge: '写真を拡大', shownIn: '写真のカラー：', viewColor: 'カラーを表示：',
      chooseSize: 'サイズを選択', add: 'カートに追加', adding: '追加中…', viewCart: 'カートを見る', soldOut: '売り切れ', notAvailable: '購入できません', checking: '販売状況を確認中…', added: 'カートに追加しました。',
      size: 'サイズ', length: '着丈', shoulder: '肩幅', chest: '身幅', sleeve: '袖丈',
      cm: '単位：cm', inch: '単位：インチ',
      unavailable: '商品情報を読み込めませんでした。時間をおいてもう一度お試しください。',
      addError: 'カートに追加できませんでした。もう一度お試しください。', cartUncertain: '追加結果を確認できませんでした。再度追加する前にカートをご確認ください。', quantityError: '数量は1以上の整数で指定してください。',
    }
  };
  const swatches = {
    black: '#222323', white: '#fafaf7', 'flower gray': '#b9bab9', 'light gray': '#c7c6c1',
    'navy blue': '#252b43', 'royal blue': '#2b45ac', 'wine red': '#692c36',
    'blackish green': '#303e37', 'mild apricot': '#dfd3be', 'light apricot': '#dfd9c6',
    'faded black': '#454546'
  };

  class ProductPreview {
    constructor(root) {
      this.root = root;
      this.q = selector => root.querySelector(selector);
      this.qa = selector => [...root.querySelectorAll(selector)];
      this.abort = new AbortController();
      this.events = { signal: this.abort.signal };
      this.text = labels[root.dataset.lang] || labels.en;
      this.base = (root.dataset.root || '/').replace(/\/?$/, '/');
      this.color = this.q('[data-color]');
      this.size = this.q('[data-size]');
      this.status = this.q('[data-status]');
      this.gallery = this.q('.kp-gallery');
      this.slide = 0;
      this.unit = root.dataset.lang === 'ja' ? 'cm' : 'inch';
      this.hasCart = false;
      this.cartItems = [];
      this.cartIds = new Set();
      this.liveVariants = new Map();
      this.linkage = 'checking';
      this.cartRevision = 0;
      this.busy = false;
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.setup();
      this.load();
    }

    listen(target, type, callback, options = {}) {
      target.addEventListener(type, callback, { ...options, ...this.events });
    }

    async json(url) {
      const response = await fetch(url, { signal: this.abort.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Product preview data unavailable');
      return response.json();
    }

    setup() {
      this.listen(this.color, 'change', () => this.showColor());
      this.listen(this.size, 'change', () => this.selection());
      this.listen(this.q('.kp-form'), 'submit', event => { event.preventDefault(); this.activate(event.submitter || this.q('[data-add]')); });
      this.qa('[data-guide]').forEach(button => this.listen(button, 'click', () => this.openDialog(this.q('.kp-guide'), button)));
      this.qa('[data-close]').forEach(button => this.listen(button, 'click', () => { this.returnToSizes = button.hasAttribute('data-return-size'); button.closest('dialog').close(); }));
      this.qa('dialog').forEach(dialog => {
        this.listen(dialog, 'close', () => { if (this.returnToSizes) { this.returnToSizes = false; this.focusSizes(); } else this.opener?.focus({ preventScroll: true }); });
        this.listen(dialog, 'click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        });
      });
      this.qa('[data-unit]').forEach(button => this.listen(button, 'click', () => this.table(button.dataset.unit)));
      this.listen(this.q('[data-bottom]'), 'click', event => {
        if (this.purchaseState().action === 'cart') return;
        event.preventDefault();
        this.activate(event.currentTarget);
      });
      this.listen(this.q('[data-gallery-back]'), 'click', () => this.goTo(this.slide - 1));
      this.listen(this.q('[data-gallery-next]'), 'click', () => this.goTo(this.slide + 1));
      this.listen(this.gallery, 'scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = 0; this.updateGallery(); });
      }, { passive: true });
      this.listen(this.gallery, 'keydown', event => {
        if (event.target !== this.gallery || !window.matchMedia('(max-width:749px)').matches) return;
        const action = { ArrowLeft: this.slide - 1, ArrowRight: this.slide + 1, Home: 0, End: this.gallery.children.length - 1 };
        if (!(event.key in action)) return;
        event.preventDefault();
        this.goTo(action[event.key]);
      });
      this.listen(this.gallery, 'pointerdown', event => { this.pointer = { x: event.clientX, y: event.clientY }; }, { passive: true });
      this.listen(this.gallery, 'pointerup', event => {
        if (this.pointer && (Math.abs(event.clientX - this.pointer.x) > 10 || Math.abs(event.clientY - this.pointer.y) > 10)) this.skipZoomUntil = Date.now() + 600;
        this.pointer = null;
      }, { passive: true });
      this.listen(this.gallery, 'pointercancel', () => { this.pointer = null; this.skipZoomUntil = Date.now() + 600; }, { passive: true });
      this.galleryWidth = this.gallery.clientWidth;
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.gallery.clientWidth;
        if (!width || width === this.galleryWidth) return;
        this.galleryWidth = width;
        this.gallery.scrollTo({ left: width * this.slide, behavior: 'instant' });
        this.updateGallery();
      });
      this.resizeObserver.observe(this.gallery);
      this.listen(window, 'pageshow', () => this.updateCart());
      this.listen(window, 'focus', () => this.updateCart());
      this.listen(document, 'cart:updated', () => this.updateCart());
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event?.source !== 'korosuke-product') this.updateCart();
        });
      }
    }

    motion() {
      return window.matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth';
    }

    focusSizes() {
      const target = this.q('[data-size-options]');
      target.scrollIntoView({ behavior: this.motion(), block: 'center' });
      const selected = target.querySelector('input:checked') || target.querySelector('input:not(:disabled)');
      selected?.focus({ preventScroll: true });
    }

    openDialog(dialog, opener) {
      if (dialog.open) return;
      this.opener = opener;
      dialog.showModal();
    }

    localized(key) {
      return this.family.translations?.[this.root.dataset.lang]?.[key] ?? this.family[key];
    }

    async load() {
      try {
        const catalog = await this.json(this.root.dataset.catalog);
        this.family = catalog.find(family => family.key === this.root.dataset.family);
        if (!this.family?.colors?.length) throw new Error('Unknown product family');
        const name = this.localized('name') || this.family.name;
        this.q('[data-title]').textContent = name;
        this.q('[data-bottom-title]').textContent = name;
        this.q('[data-series]').textContent = this.localized('collection') || '';
        this.q('[data-price]').textContent = '$' + Number(this.family.priceProposal).toFixed(2);
        this.q('[data-bottom-price]').textContent = '$' + Number(this.family.priceProposal).toFixed(2) + ' USD';
        this.q('[data-description]').textContent = this.localized('description') || '';
        this.q('[data-fabric]').textContent = this.localized('fabric') || '';
        this.q('[data-fit]').textContent = this.localized('fit') || '';
        if (this.localized('care')) this.q('[data-care]').textContent = this.localized('care');
        const features = this.localized('features') || [];
        const list = this.q('[data-features]');
        list.replaceChildren();
        features.forEach(feature => { const li = document.createElement('li'); li.textContent = feature; list.append(li); });
        list.hidden = features.length === 0;
        this.buildModelHero();
        this.buildColors();
        this.buildColorOverview();
        const params = new URL(location.href).searchParams;
        const variantId = params.get('variant');
        const colorName = params.get('color')?.toLowerCase();
        let colorIndex = this.family.colors.findIndex(color => color.variants.some(variant => String(variant.id) === variantId));
        if (colorIndex < 0 && colorName) colorIndex = this.family.colors.findIndex(color => color.name.toLowerCase() === colorName);
        this.color.value = String(Math.max(0, colorIndex));
        const initialVariant = this.family.colors[Number(this.color.value)].variants.find(variant => String(variant.id) === variantId);
        this.showColor(initialVariant?.size);
        this.table(this.unit);
        this.root.dataset.ready = 'true';
        await Promise.all([this.verifyVariants(), this.updateCart()]);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.linkage = 'unavailable';
        this.root.dataset.linkage = 'unavailable';
        this.status.textContent = this.text.unavailable;
        this.updateButtons();
      }
    }

    buildColors() {
      const holder = this.q('[data-color-options]');
      holder.replaceChildren();
      this.color.replaceChildren();
      this.family.colors.forEach((color, index) => {
        this.color.add(new Option(color.name, String(index)));
        const wrap = document.createElement('div');
        wrap.className = 'kp-choice kp-color-choice';
        const radio = document.createElement('input');
        radio.type = 'radio'; radio.name = 'kp-color-' + this.root.dataset.section;
        radio.id = 'KpColorChoice-' + this.root.dataset.section + '-' + index;
        radio.value = String(index);
        const label = document.createElement('label'); label.htmlFor = radio.id;
        const dot = document.createElement('i'); dot.setAttribute('aria-hidden', 'true');
        dot.style.setProperty('--kp-swatch', color.swatch || swatches[color.name.toLowerCase()] || '#666660');
        const name = document.createElement('span'); name.textContent = color.name;
        label.append(dot, name); wrap.append(radio, label); holder.append(wrap);
        this.listen(radio, 'change', () => { this.color.value = radio.value; this.showColor(); });
      });
    }

    wearingPhotos(color) {
      return (color?.looks || []).map(photo => typeof photo === 'string' ? { src: photo, alt: this.family.name + ' — ' + color.name + ' — ' + this.text.look } : photo).filter((photo, index, items) => photo?.src && items.findIndex(item => item?.src === photo.src) === index);
    }

    buildModelHero() {
      const hero = this.q('[data-model-hero]'), holder = this.q('[data-model-photos]');
      if (!hero || !holder) return;
      const preferred = String(this.family.defaultColor || '').toLowerCase();
      const hasLooks = color => this.wearingPhotos(color).length > 0;
      const color = this.family.colors.find(color => color.name.toLowerCase() === preferred && hasLooks(color)) || this.family.colors.find(hasLooks);
      const photos = this.wearingPhotos(color).slice(0, 2);
      holder.replaceChildren();
      hero.hidden = photos.length === 0;
      hero.dataset.count = String(photos.length);
      if (!photos.length) return;
      this.q('[data-model-caption]').textContent = this.text.shownIn + ' ' + color.name;
      photos.forEach((photo, index) => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'kp-model-photo';
        button.setAttribute('aria-label', this.text.enlarge + ' — ' + (photo.alt || this.family.name));
        const image = new Image(); image.src = photo.src; image.alt = photo.alt || this.family.name + ' — ' + color.name;
        image.width = 800; image.height = 1000; image.loading = 'eager'; image.decoding = 'async';
        if (index === 0) image.fetchPriority = 'high';
        button.append(image);
        this.listen(button, 'click', () => {
          const zoom = this.q('.kp-zoom');
          zoom.querySelector('img').src = photo.src; zoom.querySelector('img').alt = image.alt;
          this.openDialog(zoom, button);
        });
        holder.append(button);
      });
    }

    buildColorOverview() {
      const overview = this.q('[data-color-overview]'), holder = this.q('[data-color-previews]');
      if (!overview || !holder) return;
      holder.replaceChildren();
      holder.style.setProperty('--kp-color-count', String(Math.min(4, this.family.colors.length)));
      this.family.colors.forEach((color, index) => {
        if (!color.front) return;
        const button = document.createElement('button'); button.type = 'button'; button.className = 'kp-color-preview';
        button.dataset.colorPreview = String(index); button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', this.text.viewColor + ' ' + color.name);
        const image = new Image(); image.src = color.front; image.alt = ''; image.width = 240; image.height = 240; image.loading = 'lazy'; image.decoding = 'async';
        const name = document.createElement('span'); name.textContent = color.name;
        button.append(image, name);
        this.listen(button, 'click', () => {
          if (this.busy) return;
          this.color.value = String(index); this.showColor();
        });
        holder.append(button);
      });
      overview.hidden = holder.children.length < 2;
    }

    showColor(initialSize) {
      const color = this.family.colors[Number(this.color.value)];
      if (!color) return;
      const oldSize = initialSize || this.size.value;
      this.q('[data-color-name]').textContent = color.name;
      this.qa('[data-color-options] input').forEach(input => { input.checked = input.value === this.color.value; });
      this.qa('[data-color-preview]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.colorPreview === this.color.value)));
      this.size.replaceChildren(new Option(this.text.chooseSize, ''));
      const holder = this.q('[data-size-options]'); holder.replaceChildren();
      this.family.sizes.forEach((size, index) => {
        const available = color.variants.some(variant => variant.size === size);
        const option = new Option(size, size); option.disabled = !available; this.size.add(option);
        const wrap = document.createElement('div'); wrap.className = 'kp-choice kp-size-choice';
        const radio = document.createElement('input'); radio.type = 'radio';
        radio.name = 'kp-size-' + this.root.dataset.section; radio.id = 'KpSizeChoice-' + this.root.dataset.section + '-' + index;
        radio.value = size; radio.disabled = !available;
        const label = document.createElement('label'); label.htmlFor = radio.id; label.textContent = size;
        wrap.append(radio, label); holder.append(wrap);
        this.listen(radio, 'change', () => { this.size.value = radio.value; this.selection(); });
      });
      this.size.value = color.variants.some(variant => variant.size === oldSize) ? oldSize : '';
      const looks = this.wearingPhotos(color);
      const photos = [
        { src: color.front, alt: this.family.name + ' — ' + color.name + ' — ' + this.text.front },
        { src: color.back, alt: this.family.name + ' — ' + color.name + ' — ' + this.text.back }
      ].filter((photo, index, items) => photo.src && items.findIndex(item => item.src === photo.src) === index);
      this.gallery.replaceChildren();
      photos.forEach((photo, index) => {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'kp-photo';
        button.setAttribute('aria-label', this.text.enlarge + ' — ' + (photo.alt || this.family.name));
        const image = new Image(); image.src = photo.src; image.alt = photo.alt || this.family.name;
        image.width = 1400; image.height = 1400; image.decoding = 'async'; image.loading = index < 2 ? 'eager' : 'lazy';
        image.className = 'kp-photo__base';
        button.append(image);
        const wearing = index === 1 ? looks[looks.length > 1 ? 1 : 0] : null;
        if (wearing) {
          button.classList.add('kp-photo--paired');
          const overlay = new Image();
          overlay.src = wearing.src; overlay.alt = ''; overlay.setAttribute('aria-hidden', 'true');
          overlay.className = 'kp-photo__wearing'; overlay.width = 1400; overlay.height = 1400;
          overlay.loading = 'lazy'; overlay.decoding = 'async'; button.append(overlay);
        }
        this.listen(button, 'click', () => {
          if (Date.now() < (this.skipZoomUntil || 0)) return;
          const showWearing = wearing && window.matchMedia('(min-width:750px) and (hover:hover) and (pointer:fine)').matches && (button.matches(':hover') || button.matches(':focus-visible'));
          const zoom = this.q('.kp-zoom');
          zoom.querySelector('img').src = showWearing ? wearing.src : image.src;
          zoom.querySelector('img').alt = showWearing ? (wearing.alt || this.family.name) : image.alt;
          this.openDialog(zoom, button);
        });
        this.gallery.append(button);
      });
      this.slide = 0;
      this.gallery.scrollTo({ left: 0, behavior: 'instant' });
      this.updateGallery();
      this.selection();
    }

    selection() {
      const color = this.family.colors[Number(this.color.value)];
      this.selected = color.variants.find(variant => variant.size === this.size.value);
      this.q('[data-variant]').value = this.selected?.id || '';
      this.qa('[data-size-options] input').forEach(input => { input.checked = input.value === this.size.value; });
      this.status.textContent = this.linkage === 'unavailable' ? this.text.unavailable : '';
      this.updateAvailability();
      this.updateButtons();
      this.highlightSize();
    }

    goTo(index) {
      const target = Math.max(0, Math.min(index, this.gallery.children.length - 1));
      this.gallery.scrollTo({ left: this.gallery.clientWidth * target, behavior: this.motion() });
    }

    updateGallery() {
      const count = this.gallery.children.length;
      const width = this.gallery.clientWidth;
      this.slide = width && count ? Math.min(count - 1, Math.max(0, Math.round(this.gallery.scrollLeft / width))) : 0;
      this.q('[data-photo-count]').textContent = (count ? this.slide + 1 : 0) + ' / ' + count;
      this.q('[data-gallery-back]').disabled = this.slide === 0;
      this.q('[data-gallery-next]').disabled = this.slide >= count - 1;
    }

    table(unit) {
      if (!this.family?.sizeChart?.[unit]) return;
      this.unit = unit;
      const data = this.family.sizeChart[unit], table = this.q('[data-table]');
      const sizes = data[0].value, measures = data.slice(1);
      table.replaceChildren();
      const caption = document.createElement('caption'); caption.textContent = this.text[unit]; table.append(caption);
      const thead = document.createElement('thead'), header = document.createElement('tr');
      const first = document.createElement('th'); first.scope = 'col'; first.textContent = this.text.size; header.append(first);
      measures.forEach(measure => {
        const cell = document.createElement('th'); cell.scope = 'col';
        const key = measure.name.toLowerCase().startsWith('sleeve') ? 'sleeve' : measure.name.toLowerCase();
        cell.textContent = this.text[key] || measure.name; header.append(cell);
      });
      thead.append(header); table.append(thead);
      const body = document.createElement('tbody');
      sizes.forEach((size, index) => {
        const row = document.createElement('tr'); row.dataset.size = size;
        const heading = document.createElement('th'); heading.scope = 'row'; heading.textContent = size; row.append(heading);
        measures.forEach(measure => { const cell = document.createElement('td'); cell.textContent = measure.value[index]; row.append(cell); });
        body.append(row);
      });
      table.append(body);
      this.qa('[data-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.unit === unit)));
      this.highlightSize();
    }

    highlightSize() {
      this.qa('[data-table] tbody tr').forEach(row => { row.dataset.selected = String(row.dataset.size === this.size.value); });
    }

    purchaseState() {
      if (this.busy) return { action: 'wait', label: this.text.adding, disabled: true };
      const inCart = this.selected ? this.cartIds.has(String(this.selected.id)) : this.hasCart;
      if (inCart || this.cartUncertain) return { action: 'cart', label: this.text.viewCart, disabled: false };
      if (this.linkage !== 'verified') return { action: 'wait', label: this.linkage === 'checking' ? this.text.checking : this.text.notAvailable, disabled: true };
      if (![...this.liveVariants.values()].some(variant => variant.available)) return { action: 'wait', label: this.text.soldOut, disabled: true };
      if (!this.selected) return { action: 'select', label: this.text.chooseSize, disabled: false };
      if (!this.liveVariants.get(String(this.selected.id))?.available) return { action: 'wait', label: this.text.soldOut, disabled: true };
      return { action: 'add', label: this.text.add, disabled: false };
    }

    updateAvailability() {
      const color = this.family?.colors[Number(this.color.value)];
      if (!color) return;
      const disabled = size => {
        const variant = color.variants.find(variant => variant.size === size);
        return this.busy || !variant || this.linkage !== 'verified' || !this.liveVariants.get(String(variant.id))?.available;
      };
      this.qa('[data-size-options] input').forEach(input => { input.disabled = disabled(input.value); });
      [...this.size.options].forEach(option => { if (option.value) option.disabled = disabled(option.value); });
      this.qa('[data-color-options] input').forEach(input => { input.disabled = this.busy; });
      this.qa('[data-color-preview]').forEach(button => { button.disabled = this.busy; });
    }

    updateButtons() {
      const state = this.purchaseState(), add = this.q('[data-add]'), bottom = this.q('[data-bottom]');
      add.textContent = state.label;
      add.disabled = state.disabled;
      add.setAttribute('aria-busy', String(this.busy));
      bottom.textContent = state.label;
      bottom.href = state.action === 'cart' ? this.root.dataset.cartUrl : '#KpSizes-' + this.root.dataset.section;
      bottom.setAttribute('aria-disabled', String(state.disabled));
      bottom.setAttribute('aria-busy', String(this.busy));
      bottom.dataset.hasCart = String(state.action === 'cart');
    }

    activate(opener) {
      const state = this.purchaseState();
      if (state.disabled) return;
      if (state.action === 'cart') { window.location.assign(this.root.dataset.cartUrl); return; }
      if (state.action === 'select') { this.focusSizes(); return; }
      if (state.action === 'add') this.addToCart(opener);
    }

    async addToCart(opener) {
      if (this.purchaseState().action !== 'add') return;
      const form = this.q('.kp-form'), variant = this.selected;
      const quantity = Number(form.elements.quantity.value);
      if (!Number.isSafeInteger(quantity) || quantity < 1) { this.status.textContent = this.text.quantityError; return; }
      this.busy = true;
      this.status.textContent = '';
      this.updateAvailability();
      this.updateButtons();
      let beforeQuantity = null, definitiveFailure = false;
      try {
        const before = await this.updateCart();
        if (before) beforeQuantity = before.items.filter(item => String(item.variant_id) === String(variant.id)).reduce((sum, item) => sum + item.quantity, 0);
        const formData = new FormData(form);
        formData.set('id', String(variant.id));
        formData.set('quantity', String(quantity));
        const sections = typeof this.cart?.getSectionsToRender === 'function' ? this.cart.getSectionsToRender().map(section => section.id).slice(0, 5) : ['cart-icon-bubble'];
        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);
        this.cart?.setActiveElement?.(opener);
        const response = await fetch(this.base + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin', signal: this.abort.signal,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          definitiveFailure = response.status >= 400 && response.status < 500;
          throw new Error(typeof result.description === 'string' ? result.description : this.text.addError);
        }
        this.cartRevision += 1;
        this.cartIds.add(String(variant.id));
        this.hasCart = true;
        this.status.textContent = this.text.added;
        let rendered = false;
        if (typeof this.cart?.renderContents === 'function' && sections.every(id => typeof result.sections?.[id] === 'string' && result.sections[id].trim())) {
          try {
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(result);
            rendered = true;
          } catch (_) { rendered = false; }
        }
        if (!rendered) this.updateCartIcon(result.sections?.['cart-icon-bubble']);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'korosuke-product', productVariantId: variant.id, cartData: result }); } catch (_) { }
        }
        await this.updateCart();
      } catch (error) {
        if (error.name === 'AbortError') return;
        const cart = await this.updateCart();
        const afterQuantity = cart?.items.filter(item => String(item.variant_id) === String(variant.id)).reduce((sum, item) => sum + item.quantity, 0);
        if (beforeQuantity !== null && afterQuantity > beforeQuantity) {
          this.status.textContent = this.text.added;
        } else {
          this.cartUncertain = !definitiveFailure;
          this.status.textContent = definitiveFailure ? error.message || this.text.addError : this.text.cartUncertain;
        }
        if (definitiveFailure) await this.verifyVariants();
      } finally {
        this.busy = false;
        if (!this.abort.signal.aborted) { this.updateAvailability(); this.updateButtons(); }
      }
    }

    updateCartIcon(html) {
      if (typeof html !== 'string') return;
      const target = document.getElementById('cart-icon-bubble');
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (target && source) target.innerHTML = source.innerHTML;
    }

    async updateCart() {
      if (!this.family) return null;
      if (this.cartPending) return this.cartPending;
      const revision = this.cartRevision;
      this.cartPending = (async () => {
        try {
          const cart = await this.json(this.base + 'cart.js');
          if (revision !== this.cartRevision) return null;
          const ids = new Set(this.family.colors.flatMap(color => color.variants.map(variant => String(variant.id))));
          this.cartItems = cart.items;
          this.cartIds = new Set(cart.items.filter(item => ids.has(String(item.variant_id))).map(item => String(item.variant_id)));
          this.hasCart = this.cartIds.size > 0;
          return cart;
        } catch (_) { return null; }
        finally { this.cartPending = null; if (!this.abort.signal.aborted) this.updateButtons(); }
      })();
      return this.cartPending;
    }

    async verifyVariants() {
      try {
        if (this.family.priceApproved !== true) throw new Error('Price not approved');
        const handles = [...new Set(this.family.colors.map(color => color.handle))];
        const products = await Promise.all(handles.map(handle => this.json(this.base + 'products/' + encodeURIComponent(handle) + '.js')));
        const verified = new Map();
        for (const color of this.family.colors) {
          const product = products.find(product => product.handle === color.handle);
          if (String(product?.id) !== String(color.sourceProductId)) throw new Error('Source product mismatch');
          for (const variant of color.variants) {
            const live = product.variants.find(item => String(item.id) === String(variant.id));
            if (!live || live.option1 !== color.name || live.option2 !== variant.size || live.sku !== variant.sku || Math.abs(live.price - Number(variant.sourcePrice) * 100) > 0.01) throw new Error('Source variant mismatch');
            verified.set(String(variant.id), { available: live.available === true });
          }
        }
        this.liveVariants = verified;
        this.linkage = 'verified';
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.liveVariants.clear();
        this.linkage = 'unavailable';
        this.status.textContent = this.text.unavailable;
      } finally {
        this.root.dataset.linkage = this.linkage;
        this.updateAvailability();
        this.updateButtons();
      }
    }

    destroy() {
      this.abort.abort();
      this.unsubscribeCart?.();
      this.resizeObserver.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.qa('dialog[open]').forEach(dialog => dialog.close());
      delete this.root.dataset.ready;
    }
  }

  const mount = root => { if (!instances.has(root)) instances.set(root, new ProductPreview(root)); };
  document.querySelectorAll('.korosuke-product').forEach(mount);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('.korosuke-product').forEach(mount));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.korosuke-product').forEach(root => { instances.get(root)?.destroy(); instances.delete(root); }));
})();
